/* eslint-disable @typescript-eslint/consistent-type-imports, object-shorthand */
import { parse, print, type DocumentNode } from 'graphql';
import { CacheConfig, GraphQLSingularResponse, RequestParameters, Variables } from 'relay-runtime';
import { getCacheableRelaySSRResponse } from './relaySSRHydrationContext.ts';
import { RelayQueryResultCache } from './relayQueryResultCache.ts';
import { getServerStream } from '@confluence/ssr-data-streaming/entry-points/server.ts';
import { createIdentifierFromQuery } from '@confluence/graphql/entry-points/createIdentifierFromQuery.ts';

export const SSR_NAMESPACE = 'relay';
let ssrQueryResultCache: RelayQueryResultCache | null = null;
let isSSRDataLinkEnabled = false;
const originalQueryIdentifierByString = new Map<string, string>();
const originalQueryIdentifierByDocument = new WeakMap<DocumentNode, string | null>();

export interface Event {
	type: 'result';
	operation: string;
	queryId: string;
	variables: Variables;
	result: GraphQLSingularResponse;
}

function isDocumentNode(value: unknown): value is DocumentNode {
	return (
		typeof value === 'object' &&
		value !== null &&
		(value as Partial<DocumentNode>).kind === 'Document' &&
		Array.isArray((value as Partial<DocumentNode>).definitions)
	);
}

function getOriginalQueryStringIdentifier(originalQuery: string): string {
	const cachedIdentifier = originalQueryIdentifierByString.get(originalQuery);
	if (cachedIdentifier !== undefined) {
		return cachedIdentifier;
	}

	let identifier;
	try {
		identifier = createIdentifierFromQuery(print(parse(originalQuery)));
	} catch {
		identifier = createIdentifierFromQuery(originalQuery);
	}
	originalQueryIdentifierByString.set(originalQuery, identifier);
	return identifier;
}

function getOriginalQueryDocumentIdentifier(originalQuery: DocumentNode): string | null {
	const cachedIdentifier = originalQueryIdentifierByDocument.get(originalQuery);
	if (cachedIdentifier !== undefined) {
		return cachedIdentifier;
	}

	let identifier;
	try {
		identifier = createIdentifierFromQuery(print(originalQuery));
	} catch {
		identifier = null;
	}
	originalQueryIdentifierByDocument.set(originalQuery, identifier);
	return identifier;
}

function getOriginalQueryIdentifier(cacheConfig: CacheConfig): string | null {
	const originalQuery = cacheConfig.metadata?.originalQuery;
	if (typeof originalQuery === 'string') {
		return getOriginalQueryStringIdentifier(originalQuery);
	}
	if (isDocumentNode(originalQuery)) {
		return getOriginalQueryDocumentIdentifier(originalQuery);
	}
	return null;
}

function getStreamOperationIdentifier(
	request: RequestParameters,
	cacheConfig: CacheConfig,
): string | null {
	if (request.text) {
		return createIdentifierFromQuery(request.text);
	}
	if (request.id) {
		return `id:${request.id}`;
	}
	return getOriginalQueryIdentifier(cacheConfig);
}

/**
 * Read the data from the global buffer for the query id to be used by useQueryInterop to read and put into the Relay store before using fetchQuery.
 */
export const readDataFromStream = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
): GraphQLSingularResponse | null => {
	if (window.__SSR_REACT_STREAMING__ && ssrQueryResultCache) {
		const queryIdentifier = getStreamOperationIdentifier(request, cacheConfig);
		if (!queryIdentifier) {
			return null;
		}
		const result = ssrQueryResultCache.getMatchingResult(queryIdentifier, variables);
		return result;
	}
	return null;
};

/**
 * Write the data from the response to the server stream.
 *
 */
export const writeDataToStream = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
	response: GraphQLSingularResponse,
): void => {
	if (!isSSRDataLinkEnabled) {
		return;
	}

	const cacheableResponse = getCacheableRelaySSRResponse(response);
	if (!cacheableResponse) {
		return;
	}

	const serverStream = getServerStream(SSR_NAMESPACE);
	const queryIdentifier = getStreamOperationIdentifier(request, cacheConfig);
	if (queryIdentifier) {
		const event: Event = {
			type: 'result',
			queryId: request.id ?? '',
			operation: queryIdentifier,
			variables,
			result: cacheableResponse,
		};
		serverStream.emit(event);
	}
};

export function enableSSRDataLink(): void {
	isSSRDataLinkEnabled = true;
}

export function disableSSRDataLink(): void {
	isSSRDataLinkEnabled = false;
}

export const initializeSSR = (namespace: string): void => {
	if (window.__SSR_REACT_STREAMING__) {
		ssrQueryResultCache = new RelayQueryResultCache(namespace);
	}
};
